import * as yup from 'yup'
import {
  formatarDataHora,
  parseDateStringToDate as parse,
  isDate,
  validarCPF,
  validarDuasOuMaisPalavras,
} from '../formatar'
import { checkIfConsumerExists } from '../../services/api'
import { validarWaTelefoneNumero } from '../funcoes'
import { phoneValidation } from '../phoneValidation'
import { passwordValidationRegex } from '../passwordValidationRegex'

const today = new Date()

export const validationFields = {
  nome: yup
    .string()
    .required('Nome é obrigatório.')
    .min(3, 'Nome precisa ter no mínimo 3 caracteres')
    .max(60, 'Nome precisa ter no máximo 60 caracteres')
    .matches(
      /^[a-zA-ZáàâãéèêíìîóòõôúùûçÁÀÂÃÉÈÊÍÌÎÓÒÕÔÚÙÛÇ ]+$/,
      'Nome inválido. Somente letras e caracteres acentuados'
    )
    .test('nomeCompleto', 'Insira nome e sobrenome', (value) =>
      validarDuasOuMaisPalavras(value)
    ),

  cpf: yup
    .string()
    .required('CPF é obrigatório.')
    .test('is-cpf', 'CPF inválido.', (value) =>
      value ? validarCPF(value) : false
    ),

  telefone: yup
    .string()
    .required('Telefone é obrigatório.')
    .test({
      name: 'is-phone',
      message: ({ value }) => {
        return `${phoneValidation(value)?.message}`
      },
      test: (value) => (value ? phoneValidation(value)?.isValid : false),
    }),

  sexo: yup
    .string()
    .ensure()
    .required('Sexo é obrigatório')
    .oneOf(['masculino', 'feminino'], 'Sexo é obrigatório'),

  data_nascimento: yup
    .string()
    .required('Data de nascimento é obrigatória.')
    .test('minDate', 'Data de nascimento inválida.', (value) => {
      const valueInDate = parse(value, 'dd/MM/yyyy', new Date())
      const minDate = new Date()
      minDate.setFullYear(today.getFullYear() - 100)

      return isDate(valueInDate) && valueInDate > minDate
    })
    .test('maxDate', 'Idade mínima de 18 anos.', (value) => {
      const valueInDate = parse(value, 'dd/MM/yyyy', new Date())
      const maxDate = new Date()
      maxDate.setFullYear(today.getFullYear() - 18)

      return isDate(valueInDate) && valueInDate <= maxDate
    }),

  email: yup
    .string()
    .required('E-mail é obrigatório.')
    .lowercase()
    .matches(/(?![^a-z\d\-_+@\.])/, 'E-mail inválido. Somente letras, números e os caracteres: .-_+@')
    .test(
      "no-dot-or-underscore-start-end",
      "E-mail inválido. A parte local não pode começar ou terminar com '.' ou '_'. !",
      (value) => {
        if (!value) return true;
        const [local] = value.split("@");
        return local && !/^[._]|[._]$/.test(local);
      }
    )
    .test('is-email-without-double-dots', 'E-mail inválido. Não permitido conter "..". Verifique!', (value) => {
      return !/\.\./g.test(value)
    })
    .test('is-valid-mail-domain', 'E-mail inválido.', (value) => {
      const domain = value.split('@')[1];
      if (domain && /^[.-]|[.-]$/.test(domain)) {
        throw new yup.ValidationError("E-mail inválido. Não permitido iniciar ou terminar o domínio com '.' ou '-'. Verifique!");
      }

      const domainTokens = domain.split('.');
      const validDomainRegex = /^(?![\.\-])([a-z\d\-]{1,63})(?<![\.\-])$/;

      const isInvalidDomain = domainTokens.some((token) => !validDomainRegex.test(token));
      if (isInvalidDomain) {
        throw new yup.ValidationError('Domínio do e-mail inválido, verifique!');
      }

      return true;
    }),

    current_email: yup
    .string()
    .required('E-mail é obrigatório.')
    .email('E-mail inválido.')
    .lowercase()
    .matches(
      /^[a-zA-Z0-9!#\$%&'\*\+\-\/=\?\^_`{\|}~@.]+$/,
      "E-mail inválido. Somente letras, números e os caracteres: !#$%&'*+-/=?^_`{|}~@."
    ),

    password: yup
    .string()
    .required('Senha é obrigatória.')
    .min(8, 'Deve ter pelo menos 8 caracteres')
    .test(
      'password-letters-numbers',
      'Deve conter letras e números',
      (value) => {
        return passwordValidationRegex.lettersNumbers.test(value)
      }
    )
    .test(
      'special-character-password',
      'Deve conter pelo menos um caractere especial',
      (value) => {
        return passwordValidationRegex.specialCharacter.test(value)
      }
    )
    .test('password-sequences', 'Não deve conter sequências', (value) => {
      return !passwordValidationRegex.sequences.test(value)
    })
    .test('password-repetitions', 'Não deve conter repetições', (value) => {
      return !passwordValidationRegex.repetitions.test(value)
    }),

  password_confirmation: yup
    .string()
    .required('Confirmação da senha é obrigatória.')
    .oneOf([yup.ref('password')], 'As senhas devem ser iguais'),

  email_publicidade: yup.boolean(),

  wa_aceite_notificacao: yup.boolean(),

  wa_telefone: yup
    .string()
    .ensure()
    .when('$wa_aceite_notificacao', ([wa_aceite_notificacao], schema) => {
      if (!wa_aceite_notificacao) return schema

      return schema
        .required('Número é obrigatório.')
        .test(
          'is-waPhone',
          'Verifique se o número de whatsapp segue o padrão (XX) 9XXXX-XXXX.',
          (value) => (value ? validarWaTelefoneNumero(value) : false)
        )
    }),
}

export const valitationConsumerFields = {
  cpf: validationFields.cpf.test(
    'Verifica se usuário já existe',
    'Verifique o CPF e tente novamente.',
    (cpf) => checkIfConsumerExists({ cpf })
  ),
  email: validationFields.email.test(
    'Verifica se usuário já existe',
    'Verifique o E-mail e tente novamente.',
    (email) => checkIfConsumerExists({ email })
  ),
}

export const updateCadastroFields = {
  nome: validationFields.nome,
  telefone: validationFields.telefone,
  data_nascimento: validationFields.data_nascimento,
  sexo: validationFields.sexo,
  wa_aceite_notificacao: validationFields.wa_aceite_notificacao,
  wa_telefone: validationFields.wa_telefone,
}

export const createCadastroFields = {
  nome: validationFields.nome,
  cpf: valitationConsumerFields.cpf,
  telefone: validationFields.telefone,
  sexo: validationFields.sexo,
  data_nascimento: validationFields.data_nascimento,
  email: valitationConsumerFields.email,
  password: validationFields.password,
  password_confirmation: validationFields.password_confirmation,
  email_publicidade: validationFields.email_publicidade,
  wa_aceite_notificacao: validationFields.wa_aceite_notificacao,
  wa_telefone: validationFields.wa_telefone,
}

export const updateEmailFields = {
  email_atual: validationFields.current_email,
  novo_email: valitationConsumerFields.email,
  novo_email_confirmacao: validationFields.email,
  password: validationFields.password,
}

export const redefinePassword = {
  password: validationFields.password,
  password_confirmation: validationFields.password_confirmation,
}

export const updateCadastroSchema = yup.object().shape(updateCadastroFields)
export const createCadastroSchema = yup.object().shape(createCadastroFields)
export const redefinePasswordSchema = yup.object().shape(redefinePassword)
export const updateEmailSchema = yup.object().shape(updateEmailFields)
